import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Paper, Grid, Button, Tooltip } from '@mui/material';
import axios from 'axios';
import Moment from 'react-moment';
import cronstrue from 'cronstrue';
import { Root, Header, defaultResultsContainerStyles, pageContentGap } from '../../../theme';
import CatalogTab from '../../catalogmanager/containers/catalog/components/catalog_tab';
import CatalogEventResults from '../../catalogmanager/containers/catalog/components/catalog_event_results';
import EnhancedTable from '../../../components/table';
import CatalogManagerResults from '../../../components/CatalogManagerResults';
import EventModal from '../../messages/event_modal';
import ConfirmDialog from '../../../components/confirm_dialog';
import Helpers, { parseSignalrMessage } from '../../../helpers';
import Auth from '../../../auth';
import Config from '../../../config';

// EnhancedTable is `withRouter(class EnhancedTable ...)` so withRouter's TS
// overload hides the actual `config`/`template`/`history` surface.
// CatalogManagerResults / CatalogEventResults are .js / loosely-typed default
// exports. Cast all three to permissive component types so this .tsx file
// builds under fork-ts-checker. Mirrors the pattern from attachments_v2.tsx.
const TypedEnhancedTable = EnhancedTable as React.ComponentType<any>;
const TypedCatalogManagerResults = CatalogManagerResults as React.ComponentType<any>;
const TypedCatalogEventResults = CatalogEventResults as React.ComponentType<any>;

type Job = {
    Id: string | number;
    JobName?: string;
    Schedule?: string;
    LastStart?: string;
    LastEnd?: string;
    LastSuccess?: string;
};

type Props = {
    setTitle?: (title: string, flag?: boolean) => void;
    signalr?: any;
    history: { replace: (location: { pathname: string; search: string }) => void };
    location: { pathname: string; search?: string };
};

const jobs = [
    { text: "Inventory Grabber", value: "InventoryFileGrabberJob" },
    { text: "FTP EDI Grabber", value: "EdiFileGrabberJob" },
    { text: "File Mover", value: "FtpFileMoverJob" },
    { text: "CSV/XML Inventory Exporter", value: "OutboundInventoryJob" },
    { text: "EDI Inventory Exporter", value: "OutboundEdiInventoryJob" },
    { text: "Custom XML Exporter", value: "OutboundCustomXmlJob" },
    { text: "Custom Flat File Exporter", value: "OutboundCustomFlatFileJob" },
    { text: "Custom Report Exporter", value: "OutboundCustomFlatFileReportJob" }
];

// Tag every filter as system so CatalogFilters hides them from the
// user-editable chip strip (otherwise they render as ghost chips with
// empty Field/Operator). Same pattern as attachments_v2's 'Duplicate' filter.
const eventFilter = {
    logic: 'and',
    filters: [
        { field: "CustomerViewable", operator: 'eq', value: true, system: true },
        {
            logic: 'or',
            filters: [
                { field: "EventTypeId", operator: 'eq', value: 36, system: true },
                { field: "EventTypeId", operator: 'eq', value: 64, system: true }
            ],
            system: true,
        }
    ]
};

export default function ScheduledTasks(props: Props) {
    const [currentTab, setCurrentTab] = useState<string>(() => new Helpers().getUrlObject('tab') || 'tasks');
    const [refresh, setRefresh] = useState(false);
    const [confirmJob, setConfirmJob] = useState<Job | null>(null);

    // Page-level eventId / eventViewed feed the View-button column's
    // template (defined inside historyConfig) and the page-level EventModal.
    // CatalogEventResults also renders its own EventModal internally, but
    // our column ignores the setters it passes through and uses these instead
    // so the wiring stays consistent with the old EventTable behavior.
    const [eventId, setEventId] = useState<string | number | null>(null);
    const [eventViewed, setEventViewed] = useState<boolean>(false);

    useEffect(() => {
        if (props.setTitle) {
            props.setTitle('Scheduled Tasks');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateTab = (tab: string, clearFilters: boolean) => {
        setCurrentTab(tab);
        if (clearFilters) {
            props.history.replace({
                pathname: props.location.pathname,
                search: '?tab=' + tab
            });
        } else {
            new Helpers().setUrlObject('tab', tab, props.history, props.location);
        }
    };

    const runJob = async (job: Job) => {
        await axios.get(Config.api + "/odata/Company/Functions.RunQueueJob?id=" + job.Id);
        setRefresh((r) => !r);
    };

    const showRunConfirm = useCallback((job: Job) => {
        setConfirmJob(job);
    }, []);

    const getData = useCallback(async () => {
        const values = (await axios.get(Config.api + "/odata/company/QueueJobDTOs")).data.value;
        for (let i = 0; i < values.length; i++) {
            const jobName = values[i].JobName;
            const name = jobs.find((r) => r.value === jobName);
            if (name) {
                values[i].JobName = name.text;
            }
            if (values[i].Schedule) {
                try {
                    values[i].Schedule = cronstrue.toString(values[i].Schedule);
                } catch {}
            }
        }
        return values;
    }, []);

    const onSignalr = useCallback((message: any, page: any, signalrOpts: any) => {
        message = parseSignalrMessage(message);
        if (message && message.action === 'queueJob') {
            return "refresh";
        }
        return null;
    }, []);

    const canManageSettings = Auth.hasPermission('settings/manage');

    // useState with lazy init mirrors attachments_v2.tsx so CatalogManagerResults
    // can drive column visibility / filters through setConfig. The columns
    // closure captures showRunConfirm (stable via useCallback) and the bound
    // canManageSettings, so a one-time init is sound.
    const [config, setConfig] = useState<any>(() => {
        const cols: any[] = [
            { id: 'Id', hidden: true },
            { id: 'JobName', sortable: true, label: 'Task Name', width: '20em', filterable: true },
            { id: 'Description', sortable: true, label: 'Description', filterable: true },
            { id: 'PartnerName', sortable: true, label: 'Partner', width: '20em', filterable: true },
            { id: 'Schedule', sortable: true, label: 'Schedule', width: '20em', filterable: true },
            { id: 'LastStart', type: 'date', filterable: true, sortable: true, label: 'Last Started', template: (row: Job) => (row?.LastStart ? <Moment format='MM/DD/YYYY hh:mm A'>{row.LastStart}</Moment> : ''), width: '12em' },
            { id: 'LastEnd', type: 'date', filterable: true, sortable: true, label: 'Last Ended', template: (row: Job) => (row?.LastEnd ? <Moment format='MM/DD/YYYY hh:mm A'>{row.LastEnd}</Moment> : ''), width: '12em' },
            { id: 'LastSuccess', type: 'date', filterable: true, sortable: true, label: 'Last Succeeded', template: (row: Job) => (row?.LastSuccess ? <Moment format='MM/DD/YYYY hh:mm A'>{row.LastSuccess}</Moment> : ''), width: '12em' }
        ];
        if (canManageSettings) {
            cols.push({
                command: 'commands',
                stopPropagation: 'true',
                width: '10em',
                template: (row: Job) => (
                    <Fragment>
                        <Button size='small' variant='outlined' disabled={!row?.LastStart || !row?.LastEnd} onClick={() => showRunConfirm(row)}>Go Faster</Button>
                    </Fragment>
                )
            });
        }
        return {
            columns: cols,
            getData: getData,
            order: 'asc',
            orderBy: 'Id',
            pageSize: 20,
            pageSizes: [10, 20, 50],
            refresh: false,
            title: 'Scheduled Tasks',
            keyField: 'Id',
            signalr: props.signalr,
            onSignalrUpdate: onSignalr,
            // Seed an empty filter so CatalogFilters can read table.state.filter.filters
            // on mount without crashing. Matches attachments_v2's shape (no system filters
            // needed for scheduled tasks).
            filter: { logic: 'and', filters: [] },
        };
        // Init-only; everything captured here is stable across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    // History-tab config — column set mirrors the legacy EventTable but the
    // template signatures match CatalogEventResults.generateRows, which calls
    // template(row, column, colIndex, rowIndex, addFilter, setEventId,
    // setEventViewed, setEventDialogOpen). Templates read row[id] directly.
    const [historyConfig, setHistoryConfig] = useState<any>(() => ({
        url: Config.api + '/odata/Company/VwActivityEvents',
        columns: [
            {
                id: 'EventDate',
                type: 'date',
                filterable: true,
                sortable: true,
                label: 'Date',
                template: (row: any) => (row?.EventDate ? <Moment format='MM/DD/YYYY hh:mm A'>{row.EventDate}</Moment> : ''),
                width: '18em',
            },
            { id: 'EventLevel', label: 'Severity', width: '3em', filterable: true },
            { id: 'DocType', label: 'Document Type', hidden: true },
            // DocId stayed hidden in the old EventTable when includeDocLink
            // wasn't passed in, which is our case here. Keep it hidden.
            { id: 'DocId', filterable: true, label: 'ID#', width: '5em', hidden: true },
            { id: 'Summary', label: 'Summary', filterable: true, allowStringOperators: true },
            {
                id: 'Details',
                label: 'Details',
                width: '16em',
                template: (row: any) => (
                    <Tooltip title={row?.Details || ''} enterDelay={300} disableInteractive>
                        <span>{row?.Details}</span>
                    </Tooltip>
                ),
            },
            { id: 'Viewed', hidden: true },
            { id: 'EventTypeId', hidden: true },
            {
                id: 'Id',
                width: '10em',
                stopPropagation: 'true',
                filterable: false,
                // CatalogEventResults invokes template with its own
                // setEventId/setEventViewed setters; we ignore them and use
                // the page-level setters so the page-level EventModal opens.
                template: (row: any) => (
                    <Button
                        variant='outlined'
                        onClick={() => {
                            setEventId(row?.Id);
                            setEventViewed(!!row?.Viewed);
                        }}
                    >
                        View
                    </Button>
                ),
            },
        ],
        order: 'desc',
        orderBy: 'Id',
        keyField: 'Id',
        pageSize: 10,
        pageSizes: [10, 20, 50, 100],
        title: 'Scheduled Task History',
        refresh: false,
        filter: eventFilter,
        signalr: props.signalr,
        onSignalrUpdate: onSignalr,
        // Init-only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }));

    // Mirror the page-level `refresh` toggle into config.refresh so
    // EnhancedTable picks up "Go Faster"-driven refetches via the same prop
    // contract the old plain-const config used.
    useEffect(() => {
        setConfig((prev: any) => (prev.refresh === refresh ? prev : { ...prev, refresh }));
    }, [refresh]);

    // Same propagation for the history tab — both tabs share the page-level
    // `refresh` toggle so the EventModal's onView callback (and any future
    // page-level triggers) can re-fetch event rows in place.
    useEffect(() => {
        setHistoryConfig((prev: any) => (prev.refresh === refresh ? prev : { ...prev, refresh }));
    }, [refresh]);

    return (
        <Root style={{ gap: pageContentGap, padding: '24px 14px 24px 14px' }}>
            <Grid container justifyContent="space-between" direction="row" alignItems="center" style={{ width: '100%' }}>
                <Grid item>
                    <Header>Scheduled Tasks</Header>
                </Grid>
            </Grid>

            <Grid container style={{ gap: pageContentGap, width: '100%' }}>
                <Grid item>
                    <Paper>
                        <CatalogTab
                            title="Scheduled Tasks"
                            description="View and run scheduled tasks"
                            selected={currentTab === 'tasks'}
                            onClick={() => updateTab('tasks', true)}
                        />
                    </Paper>
                </Grid>
                <Grid item>
                    <Paper>
                        <CatalogTab
                            title="Scheduled Task History"
                            description="View past task runs and events"
                            selected={currentTab === 'history'}
                            onClick={() => updateTab('history', true)}
                        />
                    </Paper>
                </Grid>
            </Grid>

            {currentTab === 'tasks' && (
                <Grid style={{ width: '100%' }}>
                    <TypedEnhancedTable
                        config={config}
                        history={props.history}
                        location={props.location}
                        template={(table: any) => (
                            <TypedCatalogManagerResults
                                isSelectable={false}
                                disableTermInUrl={true}
                                condensed={false}
                                nonCondensedRows={true}
                                history={props.history}
                                location={props.location}
                                table={table}
                                config={config}
                                setConfig={setConfig}
                                resultsContainerStyles={defaultResultsContainerStyles}
                                renderButtonFiltersAfter={false}
                                showSavedSearch={true}
                                savedSearchTableType="scheduled-tasks"
                            />
                        )}
                    />
                </Grid>
            )}
            {currentTab === 'history' && (
                <Grid style={{ width: '100%' }}>
                    <TypedEnhancedTable
                        config={historyConfig}
                        history={props.history}
                        location={props.location}
                        template={(table: any) => (
                            <TypedCatalogEventResults
                                disableOnViewAction={true}
                                history={props.history}
                                location={props.location}
                                currentCatalog={currentTab}
                                table={table}
                                config={historyConfig}
                                setConfig={setHistoryConfig}
                                showSavedSearch={true}
                                savedSearchTableType="scheduled-tasks-history"
                            />
                        )}
                    />
                </Grid>
            )}

            <EventModal
                eventId={eventId}
                viewed={eventViewed}
                onView={() => setRefresh((r) => !r)}
            />

            <ConfirmDialog
                open={confirmJob !== null}
                onClose={() => setConfirmJob(null)}
                onConfirm={async () => {
                    if (confirmJob) {
                        await runJob(confirmJob);
                    }
                    setConfirmJob(null);
                }}
                message="Are you sure you want to run this task now?"
            />
        </Root>
    );
}
